import React from 'react';
import { useInfiniteQuery } from 'react-query';

import { getPagesQuantity } from 'utils/hooksHelpers';

import { ListProgramResourcesResponsesByAccept } from '@bestdoctor/core-arch-api/api/program/types';

import {
  SearchProgramsHook,
  SearchProgramsHookData,
  SlotsPrograms,
} from 'interfaces/hooks/programsHooksInterfaces';
import { getFormattedDate } from 'utils/dateHelpers';
import { PROGRAMS_CACHE_TIME, PROGRAMS_STALE_TIME } from 'hooks/programs/programsQueryCache';
import { buildProgramsQueryParams, fetchProgramsPage, getProgramsQueryKey } from 'hooks/programs/programsQuery';

export const useSearchPrograms = ({
  company,
  period,
  periods,
  excludeEndedPeriodPrograms = false,
  selectedProgram,
  selectedServices,
  selectedСity,
  isProgramsAllowed,
  fetchAllPages = false,
  onError,
}: SearchProgramsHook): SearchProgramsHookData => {
  const companyUuid: string = company?.uuid || '';
  const periodId: string = period?.uuid || '';
  const programId: string = selectedProgram?.uuid || '';
  const valuesServices: string = selectedServices.length ? selectedServices.map((el) => el.value).join(',') : '';
  const cityId: string = selectedСity?.uuid || '';

  const programsQueryParams = buildProgramsQueryParams({
    companyUuid,
    periodId,
    periods,
    excludeEndedPeriodPrograms,
    programId,
    valuesServices,
    cityId,
  });

  const { servicePeriodIn, isAttachProgramsQuery } = programsQueryParams;
  const nonEndedPeriodUuidSet = new Set(servicePeriodIn.split(',').filter(Boolean));
  const queryKey = getProgramsQueryKey(programsQueryParams);

  const {
    isFetching,
    isLoading,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    data,
  } = useInfiniteQuery(
    queryKey,
    async ({ pageParam = 1 }) => fetchProgramsPage(programsQueryParams, pageParam),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      // isProgramsAllowed опционален: при `isProgramsAllowed && ...` значение undefined
      // короткозамыкает всё выражение в undefined, а react-query считает включённым всё,
      // что !== false. Из-за этого проверка на companyUuid не срабатывала и уходил запрос
      // за всеми программами без фильтра по компании.
      enabled: isProgramsAllowed !== false && (isAttachProgramsQuery ? !!servicePeriodIn : !!companyUuid),
      staleTime: PROGRAMS_STALE_TIME,
      cacheTime: PROGRAMS_CACHE_TIME,
      getNextPageParam: (lastPage: ListProgramResourcesResponsesByAccept['application/vnd.bestdoctor.v1-hr']) => {
        const allPage: number = getPagesQuantity(lastPage);
        const currentPage: number = lastPage.meta?.page || 0;
        const nextPage = currentPage + 1;

        return nextPage <= allPage ? nextPage : undefined;
      },
      onError,
      select: (queryData: {
        pageParams: unknown[];
        pages: ListProgramResourcesResponsesByAccept['application/vnd.bestdoctor.v1-hr'][];
      }) => {
        const currentIndex = queryData.pages.length - 1;
        const pageParams = queryData.pages[currentIndex].meta;

        const pages: ListProgramResourcesResponsesByAccept['application/vnd.bestdoctor.v1-hr']['data'][] =
          queryData.pages.map(
            (page: ListProgramResourcesResponsesByAccept['application/vnd.bestdoctor.v1-hr']) => page.data
          );

        const slotsPrograms: SlotsPrograms[] = [];

        pages.forEach((arr: ListProgramResourcesResponsesByAccept['application/vnd.bestdoctor.v1-hr']['data']) => {
          arr?.forEach((item) => {
            if (
              isAttachProgramsQuery &&
              item.service_period?.uuid &&
              !nonEndedPeriodUuidSet.has(item.service_period.uuid)
            ) {
              return;
            }

            slotsPrograms.push(item);
          });
        });

        const programs = slotsPrograms.map((item: SlotsPrograms) => {
          const startDate = getFormattedDate(item.service_period?.start_date, 'dd.MM.yyyy');
          const endDate = getFormattedDate(item.service_period?.start_date, 'dd.MM.yyyy');

          const stringServiceTypes = item.service_types?.map((el) => el.title).join('.');

          return {
            id: item.id,
            programUuid: item.uuid,
            programTitle: item.title,
            companyTitle: item.company?.title,
            serviceTypes: item.service_types,
            stringServiceTypes,
            city: item.city?.title || '',
            cityUuid: item.city?.uuid || '',
            budget: Number(item.insurance_premium) || 0,
            paidSlots: item.service_period?.paid_slots ? item.service_period.paid_slots : 0,
            medicalProgram: item.medical_program_file,
            startDate,
            endDate,
            allowedSlotPatientTypes: item.allowed_slot_patient_types?.map((type) => type.value) || null,
            period: `${startDate} - ${endDate}`,
            isVzr: item.is_vzr_cost_included_in_program_override,
            slotsCount: item.slots_count,
          };
        });

        return { programs, pageParams };
      },
    }
  );

  React.useEffect(() => {
    if (!fetchAllPages || !hasNextPage || isFetchingNextPage || error) {
      return;
    }
    void fetchNextPage();
  }, [fetchAllPages, hasNextPage, isFetchingNextPage, error, fetchNextPage]);

  return {
    isLoadingSearchPrograms: isLoading,
    isErrorSearchPrograms: error,

    fetchNextPage,

    hasNextPageSearchPrograms: hasNextPage,
    isFetchingNextPageSearchPrograms: isFetchingNextPage,
    isFetchingSearchPrograms: isFetching,

    refetchSearchPrograms: refetch,

    programs: data?.programs ?? [],
    pageParams: data?.pageParams,
  };
};
